/**
 * CreditCardSidebar - Sliding panel showing imported credit card statements
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import {
  X,
  CreditCard,
  ChevronDown,
  ChevronRight,
  Trash2,
  Calendar,
  Building2,
  Receipt,
  FileText,
  Loader2,
  AlertCircle,
  CheckCircle,
  Clock,
  ShoppingBag,
  Utensils,
  Car,
  Film,
  Fuel,
  Heart,
  Plane,
  Repeat,
  ShoppingCart,
  Wallet,
  MoreHorizontal,
} from 'lucide-react';
import { useCashflowStore } from '../../stores/cashflowStore';

// Category configuration
const CATEGORY_CONFIG = {
  food: { label: 'Yemek', icon: Utensils, color: 'text-orange-400' },
  groceries: { label: 'Market', icon: ShoppingCart, color: 'text-green-400' },
  shopping: { label: 'Alışveriş', icon: ShoppingBag, color: 'text-pink-400' },
  transport: { label: 'Ulaşım', icon: Car, color: 'text-blue-400' },
  fuel: { label: 'Akaryakıt', icon: Fuel, color: 'text-yellow-400' },
  entertainment: { label: 'Eğlence', icon: Film, color: 'text-purple-400' },
  subscriptions: { label: 'Abonelik', icon: Repeat, color: 'text-cyan-400' },
  bills: { label: 'Fatura', icon: Receipt, color: 'text-gray-400' },
  health: { label: 'Sağlık', icon: Heart, color: 'text-red-400' },
  travel: { label: 'Seyahat', icon: Plane, color: 'text-indigo-400' },
  fee: { label: 'Masraf', icon: Wallet, color: 'text-amber-400' },
  cash_advance: { label: 'Nakit', icon: Wallet, color: 'text-rose-400' },
  other: { label: 'Diğer', icon: MoreHorizontal, color: 'text-gray-400' },
};

// Status badge
const STATUS_CONFIG = {
  pending: { label: 'Bekliyor', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
  paid: { label: 'Ödendi', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
  partial: { label: 'Kısmi', color: 'bg-orange-500/20 text-orange-400', icon: AlertCircle },
};

// Format currency with proper locale
const formatCurrency = (amount, currency = 'TRY') => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const CreditCardSidebar = () => {
  const {
    isCreditCardSidebarOpen,
    closeCreditCardSidebar,
    creditCardStatements,
    creditCardTransactions,
    isLoadingCreditCards,
    loadCreditCardData,
    deleteStatement,
  } = useCashflowStore();

  const [expandedStatements, setExpandedStatements] = useState(new Set());
  const [deletingId, setDeletingId] = useState(null);

  // Load data when sidebar opens - always load to get transactions
  useEffect(() => {
    if (isCreditCardSidebarOpen) {
      // Always load to ensure transactions are fetched
      loadCreditCardData();
    }
  }, [isCreditCardSidebarOpen]);

  const toggleExpand = (statementId) => {
    setExpandedStatements(prev => {
      const next = new Set(prev);
      if (next.has(statementId)) {
        next.delete(statementId);
      } else {
        next.add(statementId);
      }
      return next;
    });
  };

  const handleDelete = async (statementId, e) => {
    e.stopPropagation();
    if (confirm('Bu ekstre ve tüm işlemleri silinecek. Emin misiniz?')) {
      setDeletingId(statementId);
      try {
        await deleteStatement(statementId);
      } finally {
        setDeletingId(null);
      }
    }
  };

  const getTransactionsForStatement = (statementId) => {
    return creditCardTransactions.filter(t => t.statement_id === statementId);
  };

  // Calculate payments made for a statement (payment transactions are negative)
  const getPaymentsMade = (statementId) => {
    return creditCardTransactions
      .filter(t => t.statement_id === statementId && t.transaction_type === 'payment')
      .reduce((sum, t) => sum + Math.abs(parseFloat(t.amount) || 0), 0);
  };

  // Calculate remaining balance for a statement
  const getRemainingBalance = (statement) => {
    const totalBalance = parseFloat(statement.total_balance) || 0;
    const paymentsMade = getPaymentsMade(statement.id);
    return Math.max(0, totalBalance - paymentsMade);
  };

  // Determine actual status based on payments
  const getActualStatus = (statement) => {
    const totalBalance = parseFloat(statement.total_balance) || 0;
    const paymentsMade = getPaymentsMade(statement.id);
    const remaining = totalBalance - paymentsMade;

    if (remaining <= 0) return 'paid';
    if (paymentsMade > 0) return 'partial';
    return 'pending';
  };

  // Group statements by card and find latest for each
  const latestStatementPerCard = creditCardStatements.reduce((acc, s) => {
    const cardKey = s.card_number_masked;
    const existingStatement = acc[cardKey];

    // Keep the one with the latest due_date
    if (!existingStatement || new Date(s.due_date) > new Date(existingStatement.due_date)) {
      acc[cardKey] = s;
    }
    return acc;
  }, {});

  // Check if a statement is the latest for its card
  const isLatestStatement = (statement) => {
    const latest = latestStatementPerCard[statement.card_number_masked];
    return latest && latest.id === statement.id;
  };

  // Calculate totals - only count LATEST statement per card to avoid double counting
  const totalBalance = Object.values(latestStatementPerCard).reduce((sum, s) => {
    const remaining = getRemainingBalance(s);
    return sum + remaining;
  }, 0);

  if (!isCreditCardSidebarOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
      onClick={closeCreditCardSidebar}
    >
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="absolute right-0 top-0 h-full w-full max-w-lg bg-gray-900 border-l border-gray-800 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-pink-500/20 rounded-lg flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-pink-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Kredi Kartı Ekstreleri</h2>
              <p className="text-sm text-gray-400">{creditCardStatements.length} ekstre</p>
            </div>
          </div>
          <button
            onClick={closeCreditCardSidebar}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto" style={{ height: 'calc(100vh - 160px)' }}>
          {isLoadingCreditCards ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-10 h-10 text-pink-400 animate-spin mb-4" />
              <p className="text-gray-400">Yükleniyor...</p>
            </div>
          ) : creditCardStatements.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mb-4">
                <FileText className="w-8 h-8 text-gray-600" />
              </div>
              <p className="text-gray-400 text-center">Henüz ekstre yüklenmemiş</p>
              <p className="text-sm text-gray-500 text-center mt-1">
                "Ekstre" butonuna tıklayarak kredi kartı ekstresi yükleyebilirsiniz
              </p>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              {creditCardStatements
                .sort((a, b) => {
                  // First sort by card number
                  if (a.card_number_masked !== b.card_number_masked) {
                    return a.card_number_masked.localeCompare(b.card_number_masked);
                  }
                  // Then by due date (latest first within same card)
                  return new Date(b.due_date) - new Date(a.due_date);
                })
                .map((statement) => {
                const isExpanded = expandedStatements.has(statement.id);
                const transactions = getTransactionsForStatement(statement.id);
                const actualStatus = getActualStatus(statement);
                const statusConfig = STATUS_CONFIG[actualStatus] || STATUS_CONFIG.pending;
                const StatusIcon = statusConfig.icon;
                const remainingBalance = getRemainingBalance(statement);
                const paymentsMade = getPaymentsMade(statement.id);
                const originalBalance = parseFloat(statement.total_balance) || 0;
                const isLatest = isLatestStatement(statement);
                const isHistorical = !isLatest && actualStatus !== 'paid';

                return (
                  <div
                    key={statement.id}
                    className={`rounded-xl overflow-hidden ${
                      isHistorical
                        ? 'bg-gray-800/30 border border-gray-700/30 opacity-60'
                        : 'bg-gray-800/50 border border-gray-700/50'
                    }`}
                  >
                    {/* Statement header */}
                    <div
                      className="p-4 cursor-pointer hover:bg-gray-800/70 transition-colors"
                      onClick={() => toggleExpand(statement.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center">
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-pink-400" />
                              <span className="font-medium text-white">
                                {statement.bank_name || 'Banka'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-400 mt-0.5">
                              **** {statement.card_number_masked?.slice(-4) || 'XXXX'}
                              {statement.card_name && ` • ${statement.card_name}`}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-lg font-semibold ${actualStatus === 'paid' ? 'text-green-400' : 'text-red-400'}`}>
                            {actualStatus === 'paid' ? 'Ödendi' : formatCurrency(remainingBalance, statement.currency)}
                          </p>
                          {paymentsMade > 0 && actualStatus !== 'paid' && (
                            <p className="text-xs text-gray-500">
                              <span className="text-green-400">-{formatCurrency(paymentsMade)}</span> ödendi
                            </p>
                          )}
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${statusConfig.color}`}>
                            <StatusIcon className="w-3 h-3" />
                            {statusConfig.label}
                          </span>
                          {isHistorical && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-gray-600/50 text-gray-400 mt-1">
                              Geçmiş dönem
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Statement details */}
                      <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Son ödeme: {statement.due_date ? format(new Date(statement.due_date), 'd MMM yyyy', { locale: tr }) : '-'}
                        </span>
                        <span>
                          {transactions.length} işlem
                        </span>
                        {statement.source_file_name && (
                          <span className="truncate max-w-[120px]" title={statement.source_file_name}>
                            {statement.source_file_name}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Expanded transactions */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="border-t border-gray-700/50"
                        >
                          {/* Balance summary */}
                          {paymentsMade > 0 && (
                            <div className="p-3 bg-gray-800/50 border-b border-gray-700/30">
                              <div className="grid grid-cols-3 gap-2 text-center">
                                <div>
                                  <p className="text-xs text-gray-500">Dönem Borcu</p>
                                  <p className="text-sm font-medium text-gray-300">{formatCurrency(originalBalance)}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500">Ödenen</p>
                                  <p className="text-sm font-medium text-green-400">-{formatCurrency(paymentsMade)}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500">Kalan</p>
                                  <p className={`text-sm font-medium ${remainingBalance <= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                    {remainingBalance <= 0 ? 'Ödendi' : formatCurrency(remainingBalance)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Transactions list */}
                          <div className="max-h-[300px] overflow-y-auto">
                            {transactions.length === 0 ? (
                              <p className="text-sm text-gray-500 p-4 text-center">
                                İşlem bulunamadı
                              </p>
                            ) : (
                              <div className="divide-y divide-gray-700/30">
                                {transactions.map((tx) => {
                                  const catConfig = CATEGORY_CONFIG[tx.ai_category] || CATEGORY_CONFIG.other;
                                  const CatIcon = catConfig.icon;
                                  const isPayment = tx.transaction_type === 'payment';

                                  return (
                                    <div key={tx.id} className="px-4 py-2 flex items-center gap-3 hover:bg-gray-800/30">
                                      <CatIcon className={`w-4 h-4 ${catConfig.color} flex-shrink-0`} />
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm text-white truncate">{tx.merchant_name}</p>
                                        <p className="text-xs text-gray-500">
                                          {format(new Date(tx.transaction_date), 'd MMM', { locale: tr })}
                                          {tx.is_installment && ` • ${tx.installment_current}/${tx.installment_total} taksit`}
                                        </p>
                                      </div>
                                      <span className={`text-sm font-medium ${isPayment ? 'text-green-400' : 'text-red-400'}`}>
                                        {isPayment ? '+' : '-'}{formatCurrency(Math.abs(tx.amount), tx.currency)}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="p-3 border-t border-gray-700/50 bg-gray-800/30">
                            <button
                              onClick={(e) => handleDelete(statement.id, e)}
                              disabled={deletingId === statement.id}
                              className="flex items-center gap-2 px-3 py-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg text-sm transition-colors disabled:opacity-50"
                            >
                              {deletingId === statement.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                              Ekstreyi Sil
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer summary */}
        {creditCardStatements.length > 0 && (
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-800 bg-gray-900">
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Toplam Bekleyen Borç</span>
              <p className="text-xl font-semibold text-red-400">
                {formatCurrency(totalBalance, 'TRY')}
              </p>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default CreditCardSidebar;
